import { ArrowRight, Star, Sparkles, RotateCcw, Pencil, UserRound, Smile, Heart } from "lucide-react";
import avatar1 from "@/assets/avatar-1.jpg";
import avatar2 from "@/assets/avatar-2.jpg";
import avatar3 from "@/assets/avatar-3.jpg";
import avatar4 from "@/assets/avatar-4.jpg";

interface BrandHeroProps {
  primaryLabel: string;
  onPrimary: () => void;
  onSecondary: () => void;
}

const BrandHero = ({ primaryLabel, onPrimary, onSecondary }: BrandHeroProps) => {
  return (
    <section className="relative w-full overflow-hidden bg-tk-primary -mt-[120px] pt-[120px] lg:-mt-[140px] lg:pt-[140px]">
      {/* Single responsive hero image. The <picture> sources are media-gated so
          the browser fetches only ONE size per viewport (no duplicate high-
          priority requests). The same <img> is restyled per breakpoint:
          mobile/tablet => faded background; desktop => anchored bottom-right. */}
      <picture>
        <source
          type="image/webp"
          media="(min-width: 1024px)"
          srcSet="/hero-brand-woman-1024.webp 1024w, /hero-brand-woman-1536.webp 1536w"
          sizes="50vw"
        />
        <source
          type="image/webp"
          srcSet="/hero-brand-woman-480.webp 480w, /hero-brand-woman-768.webp 768w"
          sizes="100vw"
        />
        <img
          src="/hero-brand-woman-768.webp"
          alt="Smiling young woman with afro hair holding a phone after taking the TonyKnows UK career assessment"
          width={1254}
          height={1254}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="pointer-events-none select-none absolute object-contain inset-x-0 bottom-0 w-full h-full object-bottom opacity-30 z-0 scale-[1.035] origin-bottom md:object-cover md:object-top md:scale-[0.8] lg:inset-x-auto lg:left-auto lg:right-[max(0px,calc((100vw-80rem)/2))] lg:top-auto lg:bottom-0 lg:w-auto lg:h-[82%] lg:max-w-none lg:object-bottom lg:opacity-100 lg:z-[5] lg:scale-100"
        />
      </picture>
      {/* Mobile + tablet darkening overlay */}
      <div
        aria-hidden="true"
        className="lg:hidden pointer-events-none absolute inset-0 bg-black/30 z-[1]"
      />

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] items-stretch">
        {/* Left column */}
        <div className="relative z-10 px-6 md:px-10 pt-10 pb-12 lg:py-14 lg:pl-[max(4rem,calc((100vw-80rem)/2))] lg:pr-16">
          <h1 className="font-editorial not-italic text-white text-[2.5rem] sm:text-6xl md:text-6xl lg:text-7xl leading-[1.05] sm:leading-tight tracking-tight mb-7">
            Are you ready to find a career that fits{" "}
            <span className="relative inline-block italic text-tk-bg/90 lg:text-[hsl(140_55%_55%)]">
              you?
              <svg
                aria-hidden="true"
                viewBox="0 0 220 14"
                preserveAspectRatio="none"
                className="hidden lg:block absolute left-0 right-0 -bottom-1 w-full h-2 pointer-events-none"
              >
                <path
                  d="M2 9 C 60 2, 150 2, 218 8"
                  fill="none"
                  stroke="hsl(140 55% 55%)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </h1>

          {/* Subhead */}
          <p className="text-base md:text-lg text-tk-bg leading-relaxed max-w-[34rem] mb-9">
            Our 3-minute assessment reveals careers where you're likely to thrive and succeed.
          </p>

          {/* Mobile-only simplified layout */}
          <div className="lg:hidden">
            <button
              onClick={onPrimary}
              className="group w-auto md:w-1/2 inline-flex items-center justify-center gap-2 bg-tk-bg text-tk-primary px-6 py-4 rounded-full text-base font-medium hover:bg-tk-bg/90 transition-all shadow-[0_12px_30px_-12px_rgba(0,0,0,0.4)] mb-8"
            >
              <Sparkles className="h-5 w-5" />
              <span>{primaryLabel}</span>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
            </button>

            <div className="mb-6">
              <div className="flex items-center gap-2 mb-1">
                <span className="flex items-center gap-0.5 text-amber-400">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="h-4 w-4 fill-current" />
                  ))}
                </span>
                <span className="text-base font-semibold text-tk-bg tabular-nums">4.8/5</span>
              </div>
              <p className="text-sm text-tk-bg">from 15,000+ users</p>
            </div>

            <div className="border-t border-tk-bg/20 pt-5 flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-tk-bg/40 text-tk-bg shrink-0">
                <UserRound className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-tk-bg leading-tight">Already started?</p>
                <div className="flex items-center gap-2 text-sm text-tk-bg mt-0.5">
                  <button onClick={onSecondary} className="underline underline-offset-4 hover:text-tk-bg">
                    Retake Assessment
                  </button>
                  <span className="text-tk-bg">|</span>
                  <button onClick={onSecondary} className="underline underline-offset-4 hover:text-tk-bg">
                    Edit Answers
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop layout (unchanged) */}
          <div className="hidden lg:block">
            {/* Primary CTA */}
            <div className="mb-6">
              <button
                onClick={onPrimary}
                className="group inline-flex items-center gap-2 bg-tk-bg text-tk-primary pl-6 pr-7 py-4 rounded-full text-base font-medium hover:bg-tk-bg/90 transition-all shadow-[0_12px_30px_-12px_rgba(0,0,0,0.4)]"
              >
                <Sparkles className="h-5 w-5" />
                <span>{primaryLabel}</span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>

            {/* Ghost action links */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mb-9">
              <button
                onClick={onSecondary}
                className="inline-flex items-center gap-2 text-sm text-tk-bg hover:text-tk-bg transition-colors"
              >
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-tk-bg/15 text-tk-bg">
                  <RotateCcw className="h-3.5 w-3.5" />
                </span>
                Retake assessment
              </button>
              <span className="hidden sm:block h-4 w-px bg-tk-bg/25" />
              <button
                onClick={onSecondary}
                className="inline-flex items-center gap-2 text-sm text-tk-bg hover:text-tk-bg transition-colors"
              >
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-tk-bg/15 text-tk-bg">
                  <Pencil className="h-3.5 w-3.5" />
                </span>
                Edit my answers
              </button>
            </div>

            {/* Simple stars + count */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="flex items-center gap-0.5 text-amber-400">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="h-5 w-5 fill-current" />
                  ))}
                </span>
                <span className="text-lg font-semibold text-tk-bg tabular-nums">4.8/5</span>
              </div>
              <p className="text-sm text-tk-bg">from 15,000+ users</p>
            </div>
          </div>
        </div>

        {/* Right column - spacer that holds the grid width; image is absolutely positioned on the section so it can extend behind the stats bar */}
        <div className="hidden lg:block relative h-[480px] lg:h-[640px] w-full" aria-hidden="true" />
      </div>

      {/* Desktop gradient circle behind the woman */}
      <div
        aria-hidden="true"
        className="hidden lg:block pointer-events-none absolute bottom-0 z-[3] h-[680px] w-[680px] translate-x-[12%] translate-y-[8%] rounded-full right-[max(0px,calc((100vw-80rem)/2))]"
        style={{
          background:
            "radial-gradient(circle at 50% 45%, hsl(140 45% 30% / 0.95) 0%, hsl(140 50% 22% / 0.7) 45%, hsl(140 55% 15% / 0) 72%)",
        }}
      />

      {/* Desktop hero image is rendered by the single <picture> at the top of this section. */}







      {/* Desktop-only bottom stats bar */}
      <div className="hidden lg:block relative z-10 px-6 md:px-10 pb-8 lg:px-[max(4rem,calc((100vw-80rem)/2))]">
        <div className="rounded-2xl bg-black/20 backdrop-blur-sm border border-white/10 px-8 py-6">
          <div className="grid grid-cols-3 gap-6 divide-x divide-white/10">
            {[
              { icon: Star, value: "4.8/5", label: "Average rating" },
              { icon: Smile, value: "10,000+", label: "Reviews from our users" },
              { icon: Heart, value: "98%", label: "Would recommend TonyKnows" },
            ].map(({ icon: Icon, value, label }, i) => (
              <div key={i} className={`flex items-center gap-4 ${i === 0 ? "" : "pl-6"}`}>
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-tk-bg shrink-0">
                  <Icon className="h-5 w-5 text-accent-orange" strokeWidth={2} />
                </span>
                <div>
                  <p className="text-2xl font-semibold text-tk-bg leading-tight">{value}</p>
                  <p className="text-sm text-tk-bg">{label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandHero;
